import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Tab, Tabs, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, FormControl, InputLabel, Select, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton } from '@mui/material';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { UpdateAllocationRequest, LocationModel, CommodityModel, BudgetModel, AllocationModel, PositionModel, PositionType, CustomerReportModel, ReportType, AuditTrailModel, GetCustomerSettingsResponse, Side, UpdateDealTicketRequest, UpdatePositionModel, CounterpartyModel, Unit, UpdateBudgetRoundRequest, UpdateBudgetsRequest, UpdateBudgetModel, BudgetRoundModel, DeliveryModel } from '../../../../Generated/Commodity-Management-Api';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { StoneXLoading, StoneXRow, StoneXDivider } from '../../../StoneX';
import { useLoadingState } from '../../../../Hooks';
import BudgetTable from '../Budgets/ViewBudgetsPage/BudgetTable';
import { BudgetFilters, BudgetRow } from '../Budgets/Types';
import { GroupingOption } from '../../../../Types';
import { ModelToRowTransformer } from '../../../../Helpers/ModelToRowTransformer';
import {
    convertVolume,
    convertPrice,
    convertPositionToUnit,
    getBudgetMonthsForContractDate,
    getMappingsForCommodity,
    getCurrentFiscalYear,
    getFiscalYearMonths,
    formatFiscalYearLabel,
    parseFiscalYearLabel,
    monthSortKey,
} from '../../../../Helpers';
import { getCurrentForecastForMonth } from './BudgetRoundHelpers';
import dayjs from 'dayjs';
import PositionTable from '../Positions/ViewPositionsPage/PositionTable';
import { PositionRow } from '../Positions/ViewPositionsPage/Types';
import { deepMerge } from '../../../../Helpers/ObjectHelper';
import { PositionReportColumn, PositionReportConfiguration, PositionReportUserOverride } from '../Positions/ViewPositionsPage/Types';
import { StoneXNumber, StoneXTextField, StoneXDatePicker } from '../../../StoneXMui';
import { useSnackbar } from 'notistack';
import './Dashboard.css';
import AddIcon from '@mui/icons-material/Add';
import AddSupplierModal from './AddSupplierModal';
import MissingPricingComponentsPrompt from './MissingPricingComponentsPrompt';
import EFPPositionsModal from './EfpFutures';
import OffsetPositionsModal from './OffsetFutures';
import ForecastTable, { ForecastData } from './ForecastTable';
import DocumentsTab from './Documents';
import PositionsTab from './Positions';
import SummaryCards from './Summary';
import Ledger from './Ledger';
import {
    CommodityContractMonthMappingModel,
    listCommodityContractMonthMappings,
} from '../../../../Apis/ContractMonthMappingApi';
import { UpdatePositionsModal } from '../Positions/UpdatePositionsPage/UpdatePositionsModal';
import PositionValidationModal from '../Positions/ViewPositionsPage/PositionValidationModal';
import ViewAuditTrailModal from '../AuditTrail/AuditTrailModal';
import { StoneXGrid } from '../../../StoneX';
import { str } from 'ajv';
import DeliveriesData, { mapBaseUnitToUnit } from './DeliveriesData';
import FuturesAllocationReport from './FuturesAllocationReport';
import { readFuturesAllocationSettings } from './FuturesAllocationSettings';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const Dashboard: React.FC = () => {
    const budgetsLoadingState = useLoadingState();
    const allocationsLoadingState = useLoadingState();
    const { locationId } = useParams<{ locationId: string }>();
    const { locations, commodities: allCommodities, counterparties: allCounterparties, units: units } = useApplicationSelector((state: RootState) => state.commodityManagement.customerData) || {};
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const [childLocations, setChildLocations] = useState<LocationModel[]>([]);
    const [commoditiesByLocation, setCommoditiesByLocation] = useState<Record<number, CommodityModel[]>>({});
    const [budgets, setBudgets] = useState<BudgetModel[]>([]);
    const [latestForecast, setLatestForecast] = useState<BudgetModel[]>([]);
    // Needed to spread contract-month futures volume across the budget months it
    // covers. Fetched once for the page: it is site-wide settings, not per location.
    const [contractMonthMappings, setContractMonthMappings] = useState<CommodityContractMonthMappingModel[]>([]);
    const [allocations, setAllocations] = useState<AllocationModel[]>([]);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const loadingState = useLoadingState();

    const [allPositions, setAllPositions] = useState<PositionModel[]>([]);

    const [locationTab, setLocationTab] = useState(Number(queryParams.get('locationTab')) || 0);
    const [commodityTab, setCommodityTab] = useState(Number(queryParams.get('commodityTab')) || 0);
    const [featureTab, setFeatureTab] = useState(Number(queryParams.get('featureTab')) || 0);

    const customerReports = useApplicationSelector((state: RootState): CustomerReportModel[] => state.commodityManagement.customerReports) || [];
    const customerReport = customerReports.find((x: any) => x.reportType === ReportType.Position);
    const reportConfiguration = useMemo(() => customerReport ? JSON.parse(customerReport.configuration!) as PositionReportConfiguration : {} as PositionReportConfiguration, [customerReport]);
    const userOverrides = useMemo(() => customerReport?.userOverrides ? JSON.parse(customerReport.userOverrides) as PositionReportUserOverride : undefined, [customerReport]);
    const reportSettings = useMemo(() => deepMerge(reportConfiguration, userOverrides), [reportConfiguration, userOverrides]);
    // The Futures Allocation report's settings ride along in the Position report configuration.
    const futuresAllocationSettings = useMemo(() => readFuturesAllocationSettings(customerReport?.configuration), [customerReport?.configuration]);
    const auditLoadingState = useLoadingState();
    const columnsToShow = useMemo(getColumnsToShow, [reportSettings]);
    const [editDealTicketModalOpen, setEditDealTicketModalOpen] = useState(false);
    const [editDealTicketId, setEditDealTicketId] = useState<number | undefined>();
    const [validationModalOpen, setValidationModalOpen] = useState(false);
    const [validationDealTicketId, setDealTicketValidationId] = useState<number | undefined>();
    const [auditTrailModalIsOpen, setAuditTrailModalIsOpen] = useState(false);
    const [AuditTrailModels, setAuditTrialChangeEvents] = useState<AuditTrailModel[]>([]);
    const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);
    const [selectedFiscalYear, setSelectedFiscalYear] = useState<string>('');
    const [efpModalOpen, setEfpModalOpen] = useState(false);
    const [efpPositions, setEfpPositions] = useState<PositionModel[]>([]);
    const [offsetModalOpen, setOffsetModalOpen] = useState(false);
    const [offsetPositions, setOffsetPositions] = useState<PositionModel[]>([]);
    const [addSupplierModalOpen, setAddSupplierModalOpen] = useState(false);
    const [detailsVisibility, setDetailsVisibility] = useState<Record<string, boolean>>({});
    const [selectedDisplayUnit, setSelectedDisplayUnit] = useState<Unit | undefined>();
    const [customDataKeys, setCustomDataKeys] = useState<string[]>([]);
    const [reportView, setReportView] = useState<'deliveries' | 'futuresAllocation'>('futuresAllocation');


    const featureTabs = ["Dashboard", "Budget", "Forecasts", "Ledger", "Positions", "Reports", "Documents"];

    // The fiscal year today falls into, per the customer's fiscal month offset.
    const currentFiscalYear = useMemo(
        () => (customerSettings ? getCurrentFiscalYear(customerSettings.fiscalMonthOffset) : undefined),
        [customerSettings]
    );

    const fiscalYearOptions = useMemo(() => {
        if (!customerSettings || currentFiscalYear == null) return [];
        const fiscalMonthOffset = customerSettings.fiscalMonthOffset ?? 0;

        // Window the dropdown around the current fiscal year rather than a fixed
        // start year, so it keeps working as time moves forward. 2025 is the
        // earliest year with data, so never offer anything before it.
        const firstYear = Math.max(2025, currentFiscalYear - 2);
        const lastYear = currentFiscalYear + 2;

        const years: string[] = [];
        for (let year = firstYear; year <= lastYear; year++) {
            years.push(formatFiscalYearLabel(year, fiscalMonthOffset));
        }
        return years;
    }, [customerSettings, currentFiscalYear]);

    useEffect(() => {
        if (fiscalYearOptions.length === 0 || selectedFiscalYear || currentFiscalYear == null) return;
        const currentLabel = formatFiscalYearLabel(currentFiscalYear, customerSettings?.fiscalMonthOffset);
        setSelectedFiscalYear(fiscalYearOptions.includes(currentLabel) ? currentLabel : fiscalYearOptions[0]);
    }, [fiscalYearOptions, selectedFiscalYear, currentFiscalYear, customerSettings]);

    useEffect(() => {
        if (locationId && locations) {
            const children = locations.filter((loc: LocationModel) => loc.parentLocationId === Number(locationId));
            setChildLocations(children);
        }
    }, [locationId, locations]);

    // The 12 budget months covered by the selected fiscal year, in order.
    // Everything on the page - the API filters, the summary cards, the ledger -
    // is driven off this single range so they can't drift apart.
    const fiscalYearMonths = useMemo(() => {
        if (!selectedFiscalYear || !customerSettings) return [];
        const fiscalYear = parseFiscalYearLabel(selectedFiscalYear);
        if (fiscalYear == null) return [];
        return getFiscalYearMonths(fiscalYear, customerSettings.fiscalMonthOffset);
    }, [selectedFiscalYear, customerSettings]);

    const fiscalYearRange = useMemo(() => {
        if (fiscalYearMonths.length === 0) return null;
        const first = fiscalYearMonths[0];
        const last = fiscalYearMonths[fiscalYearMonths.length - 1];
        return { fromYear: first.year, fromMonth: first.month, toYear: last.year, toMonth: last.month };
    }, [fiscalYearMonths]);

    // Site-wide settings rather than per-location data, so this is independent of the
    // fiscal year and location the page is showing. An empty list is a working state -
    // it means no commodity is mapped, and futures then sit on their own contract month
    // exactly as they did before.
    useEffect(() => {
        let cancelled = false;

        listCommodityContractMonthMappings(null)
            .then(response => {
                if (!cancelled) setContractMonthMappings(response.data.rows ?? []);
            })
            .catch(error => console.error('Failed to load contract month mappings', error));

        return () => { cancelled = true; };
    }, []);

    const fetchData = async () => {
        if (!selectedFiscalYear || !customerSettings || !fiscalYearRange) return;

        setLoadingData(true);
        try {
            const { fromYear: fromBudgetYear, fromMonth: fromBudgetMonth, toYear: toBudgetYear, toMonth: toBudgetMonth } = fiscalYearRange;

            const budgetPromise = CommodityManagementApi.listBudgets(
                budgetsLoadingState.setLoadingState,
                undefined,
                fromBudgetYear,
                fromBudgetMonth,
                undefined,
                toBudgetYear,
                toBudgetMonth,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                true,
                true,
                true,
                true
            );
            const allocationPromise = CommodityManagementApi.listAllocations(allocationsLoadingState.setLoadingState);

            const [budgetResponse, allocationResponse] = await Promise.all([budgetPromise, allocationPromise]);

            const budgetRows = budgetResponse.data.rows || [];
            setBudgets(budgetRows);
            setAllocations(allocationResponse.data.rows || []);

            CommodityManagementApi.listPositions(
                loadingState.setLoadingState,
                undefined, //mtmDate
                false, //include expiration dates
                true, //include market prices
                false, // don't use display units - use original units
                undefined, // no scenario market prices
                undefined, //commodityFilter,
                undefined, //locationFilter,
                undefined, //counterpartyFilter,
                undefined, //trade start date
                undefined, //trade end date
                fromBudgetYear,
                fromBudgetMonth,
                undefined, //label (ignore)
                toBudgetYear,
                toBudgetMonth,
                undefined, // label
                undefined, // contract month start filter year
                undefined, // contract month start filter month
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                false,
                true
            ).then((q) => {
                setAllPositions(q.data.rows ?? []);
            }).catch((error) => {
                // This call is deliberately not awaited, so without a catch a
                // failure became an unhandled rejection and left allPositions
                // holding the previously selected fiscal year's data.
                console.error('Failed to fetch positions', error);
                setAllPositions([]);
            });

            if (childLocations.length > 0) {
                const commoditiesMap: Record<number, CommodityModel[]> = {};
                for (const loc of childLocations) {
                    const budgetCommodityIds = Array.from(new Set(budgetRows.filter(budget => budget.location.locationId === loc.locationId).map(budget => budget.commodity.commodityId)));
                    const locationCommodities = allCommodities?.filter((cmd: CommodityModel) => budgetCommodityIds.includes(cmd.commodityId)) || [];
                    commoditiesMap[loc.locationId] = locationCommodities;
                }
                setCommoditiesByLocation(commoditiesMap);
            }
        } catch (error) {
            console.error("Failed to fetch data", error);
            setBudgets([]);
            setAllocations([]);
            setCommoditiesByLocation({});
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        if (childLocations.length > 0 && selectedFiscalYear) {
            fetchData();
        }
    }, [childLocations, allCommodities, selectedFiscalYear]);

    function getColumnsToShow() {
        return reportSettings.columns?.active;
    }

    function editDealTicket(dealTicketId?: number) {
        // This will work as add deal ticket if dealTicketId is undefined
        setEditDealTicketId(dealTicketId);
        setEditDealTicketModalOpen(true);
    }
    function setPositionValidation(dealTicketId: number) {
        setValidationModalOpen(true);
        setDealTicketValidationId(dealTicketId);
    }
    function viewAuditTrail(positionId: number) {
        setAuditTrailModalIsOpen(true);
        CommodityManagementApi.getPositionAuditTrail(auditLoadingState.setLoadingState, positionId.toString()).then(q => {
            setAuditTrialChangeEvents(q.data.rows ?? []);
        });
    }
    
    function closeAuditTrailModal() {
        setAuditTrailModalIsOpen(false);
        setAuditTrialChangeEvents([]);
    }

    async function onDealTicketSave(dealTicketId: number) {
        await CommodityManagementApi.listPositions(
            undefined,
            undefined, //mtmDate
            false, //include expiration dates
            false, //include market prices
            false, // don't use display units - use original units
            undefined, // no scenario market prices
            undefined, //commodityFilter,
            undefined, //locationFilter,
            undefined, //counterpartyFilter,
            undefined, //trade start date
            undefined, //trade end date
            undefined, // startDate.year,
            undefined, // startDate.month,
            undefined, //label (ignore)
            undefined, // endDate.year,   //get all forward positions
            undefined, // endDate.month,  //get all forward positions
            undefined, // label
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            dealTicketId, // filter by deal ticket id
        ).then((q) => {
            const newPositions = q.data.rows ?? [];
            setEditDealTicketModalOpen(false);
        });
    }

    function onDealTicketDelete(dealTicketId: number) {
        setEditDealTicketModalOpen(false);
    }

    function onValidationUpdated(dealTicketId: number, validatedPositionIds: number[]) {
        setAllPositions(previous => previous.map(q => {
            if (q.dealTicketId === dealTicketId) {
                q.isValidated = !!validatedPositionIds.find((r) => r === q.positionId);
            }
            return q;
        }))
    }

    const handleLocationChange = (event: React.SyntheticEvent, newValue: number) => {
        setLocationTab(newValue);
        setCommodityTab(0);
        setFeatureTab(0);
        setSelectedDisplayUnit(undefined);
        const newParams = new URLSearchParams();
        newParams.set('locationTab', newValue.toString());
        navigate({ search: newParams.toString() });
    };

    const handleCommodityChange = (event: React.SyntheticEvent, newValue: number) => {
        setCommodityTab(newValue);
        setFeatureTab(0);
        setSelectedDisplayUnit(undefined);
        const newParams = new URLSearchParams(location.search);
        newParams.set('commodityTab', newValue.toString());
        newParams.delete('featureTab');
        navigate({ search: newParams.toString() });
    };

    const handleFeatureChange = (event: React.SyntheticEvent, newValue: number) => {
        setFeatureTab(newValue);
        const newParams = new URLSearchParams(location.search);
        newParams.set('featureTab', newValue.toString());
        navigate({ search: newParams.toString() });
    };

    const handleViewAuditTrail = (budgetId: number) => {
        console.log('View audit trail for budget:', budgetId);
    };

    const handleEditBudget = (budgetFilter: BudgetFilters) => {
        console.log('Edit budget:', budgetFilter);
    };

    const selectedLocation = childLocations[locationTab];
    const commoditiesForSelectedLocation = selectedLocation ? commoditiesByLocation[selectedLocation.locationId] : [];
    const selectedCommodity = commoditiesForSelectedLocation?.[commodityTab];

    const getConvertedValue = (value: number | undefined | null, fromUnit: Unit | undefined | null, bushelVolume: number | undefined | null): number => {
        if (value == null) return 0;
        if (!fromUnit || !selectedDisplayUnit) return value;
        return convertVolume(value, fromUnit, selectedDisplayUnit, bushelVolume!);
    };

    const getConvertedPrice = (value: number | undefined | null, fromUnit: Unit | undefined | null, bushelVolume: number | undefined | null): number => {
        if (value == null) return 0;
        if (!fromUnit || !selectedDisplayUnit) return value;
        return convertPrice(value, fromUnit, selectedDisplayUnit, bushelVolume!);
    };

    const displayedBudgets = useMemo(() => {
        if (!selectedDisplayUnit) return budgets;
        return budgets.map(b => ({
            ...b,
            volume: getConvertedValue(b.volume, b.unit, b.budgetPricingStrategy.bushelVolume),
            price: getConvertedPrice(b.price, b.unit, b.budgetPricingStrategy.bushelVolume),
            marketPrice: getConvertedPrice(b.marketPrice, b.unit, b.budgetPricingStrategy.bushelVolume)
        }));
    }, [budgets, selectedDisplayUnit]);

    const displayedPositions = useMemo(() => {
        if (!selectedDisplayUnit) return allPositions;
        return allPositions.map(p => convertPositionToUnit(p, selectedDisplayUnit));
    }, [allPositions, selectedDisplayUnit]);

    const allBudgets = useMemo(() => {
        if (!selectedCommodity) return [];
        const childLocationIds = childLocations.map(l => l.locationId);
        return displayedBudgets.filter(b => childLocationIds.includes(b.location.locationId) && b.commodity.commodityId === selectedCommodity.commodityId);
    }, [displayedBudgets, childLocations, selectedCommodity]);

    const filteredBudgets = selectedLocation && selectedCommodity
        ? displayedBudgets.filter(b => b.location.locationId === selectedLocation.locationId && b.commodity.commodityId === selectedCommodity.commodityId && b.budgetRoundModel.budgetRoundName === "Base")
        : [];

    // One row per budget month of the selected fiscal year, taken from the most
    // recent budget round that has a value for that month. Resolving this per
    // month matters: a forecast round entered for only part of the year used to
    // collapse the whole summary down to the months that round happened to
    // cover, because the old filter matched on a single global entryDate.
    useEffect(() => {
        if (!selectedLocation || !selectedCommodity || fiscalYearMonths.length === 0) {
            setLatestForecast([]);
            return;
        }

        const relevantBudgets = displayedBudgets.filter(
            b => b.location.locationId === selectedLocation.locationId && b.commodity.commodityId === selectedCommodity.commodityId
        );

        setLatestForecast(
            fiscalYearMonths
                .map(({ year, month }) => getCurrentForecastForMonth(relevantBudgets, year, month))
                .filter((b): b is BudgetModel => !!b)
        );
    }, [selectedLocation, selectedCommodity, displayedBudgets, fiscalYearMonths]);

    const budgetTableRows: BudgetRow[] = filteredBudgets.map(x => ({ path: [], budgetLabel: '', ...x }) as unknown as BudgetRow);

    // Filter out undefined groupings
    // const selectedGroupings = [reportSettings?.behavior?.grouping?.[0], reportSettings?.behavior?.grouping?.[1]].filter(x => !!x).map(x => x!);
    const selectedGroupings: GroupingOption[] = ['Commodity'];

    // Update the path for each row based on groupings
    ModelToRowTransformer.updatePaths(budgetTableRows, selectedGroupings);

    const rawFilteredAllocations = useMemo(() => {
      return selectedLocation && selectedCommodity
        ? allocations.filter(a => a.location.locationId === selectedLocation.locationId && a.commodity.commodityId === selectedCommodity.commodityId)
        : [];
    }, [allocations, selectedLocation, selectedCommodity]);

    useEffect(() => {
        if (rawFilteredAllocations.length > 0 && !selectedDisplayUnit) {
             setSelectedDisplayUnit(rawFilteredAllocations[0].unit);
        }
    }, [rawFilteredAllocations, selectedDisplayUnit]);

    const filteredAllocations = useMemo(() => {
        if (!selectedDisplayUnit) return rawFilteredAllocations;

        return rawFilteredAllocations.map(alloc => {
            // Get bushelVolume from the base budget's pricing strategy
            const baseBudget = alloc.budgets.find(b => b.budgetRoundModel.budgetRoundName === "Base");
            const bushelVolume = baseBudget?.budgetPricingStrategy.bushelVolume;

            let monthlyVolume = alloc.monthlyVolume;
            if (monthlyVolume) {
                 try {
                     const mv = JSON.parse(monthlyVolume);
                     const newMv: any = {};
                     for (const k in mv) {
                         newMv[k] = getConvertedValue(mv[k], alloc.unit, bushelVolume);
                     }
                     monthlyVolume = JSON.stringify(newMv);
                 } catch {}
            }
             
            return {
                ...alloc,
                monthlyVolume: monthlyVolume,
                positions: alloc.positions.map(p => convertPositionToUnit(p, selectedDisplayUnit)),
                budgets: alloc.budgets.map(b => ({
                    ...b,
                    volume: getConvertedValue(b.volume, b.unit, b.budgetPricingStrategy.bushelVolume),
                    price: getConvertedPrice(b.price, b.unit, b.budgetPricingStrategy.bushelVolume),
                    marketPrice: getConvertedPrice(b.marketPrice, b.unit, b.budgetPricingStrategy.bushelVolume)
                })),
                deliveries: alloc.deliveries?.map(d => ({
                    ...d,
                    quantity: getConvertedValue(d.quantity, mapBaseUnitToUnit(d.baseUnitofMeasure), bushelVolume)
                }))
            };
        });
    }, [rawFilteredAllocations, selectedDisplayUnit]);


    const processedAllocations = filteredAllocations.map(alloc => {
        const months = JSON.parse(alloc.monthlyVolume || '{}');
        const monthKeys = Object.keys(months).sort();
        const startMonth = monthKeys.length > 0 ? dayjs(monthKeys[0]).format('MMMM YYYY') : 'N/A';
        const endMonth = monthKeys.length > 0 ? dayjs(monthKeys[monthKeys.length - 1]).format('MMMM YYYY') : 'N/A';
        return {
            ...alloc,
            startMonth,
            endMonth,
        };
    });

    const dashboardPositionsSummary = useMemo(() => {
        if (!selectedLocation) return [];
        const budgetDates = new Set(filteredBudgets.map(b => `${b.budgetDate.year}-${b.budgetDate.month}`));
        const relevantPositions = displayedPositions.filter(p =>
            budgetDates.has(`${p.budgetDate.year}-${p.budgetDate.month}`) && p.positionType === PositionType.Future && p.location.locationId === selectedLocation.locationId
        );

        const monthlyBudgetVolume = filteredBudgets.reduce((acc, budget) => {
            const monthKey = dayjs(`${budget.budgetDate.year}-${budget.budgetDate.month}-01`).format('MMM-YY');
            if (!acc[monthKey]) {
                acc[monthKey] = 0;
            }
            acc[monthKey] += budget.volume ?? 0;
            return acc;
        }, {} as Record<string, number>);

        const monthlyAllBudgetVolume = allBudgets.reduce((acc, budget) => {
            const monthKey = dayjs(`${budget.budgetDate.year}-${budget.budgetDate.month}-01`).format('MMM-YY');
            if (!acc[monthKey]) {
                acc[monthKey] = 0;
            }
            acc[monthKey] += budget.volume ?? 0;
            return acc;
        }, {} as Record<string, number>);

        const summary = relevantPositions.reduce((acc, pos) => {
            const monthKey = dayjs(`${pos.budgetDate.year}-${pos.budgetDate.month}-01`).format('MMM-YY');
            if (!acc[monthKey]) {
                acc[monthKey] = { month: monthKey, sortKey: monthSortKey(pos.budgetDate), totalVolumeSide: 0, totalMtm: 0, budgetVolume: monthlyBudgetVolume[monthKey], allBudgetVolume: monthlyAllBudgetVolume[monthKey], totalValue: 0 || 0 };
            }
            acc[monthKey].totalVolumeSide += (pos.volume ?? 0) * (pos.side == Side.Buy ? 1 : -1);
            acc[monthKey].totalMtm += pos.markToMarketValue ?? 0;
            acc[monthKey].totalValue += (pos.volume ?? 0) * (pos.price ?? 0) * (pos.side == Side.Buy ? 1 : -1);
            return acc;
        }, {} as Record<string, { month: string, sortKey: number, totalVolumeSide: number, totalMtm: number, budgetVolume: number, allBudgetVolume: number, totalValue: number }>);

        return Object.values(summary).sort((a, b) => a.sortKey - b.sortKey);
    }, [displayedPositions, filteredBudgets, selectedLocation, allBudgets]);

    const positionsByAllocation = useMemo(() => filteredAllocations
        .flatMap(a => a.positions.map(p => ({ ...p })))
        .filter(p => {
            if (!fiscalYearRange) return true;
            const y = p.budgetDate?.year;
            const m = p.budgetDate?.month;
            if (y == null || m == null) return false;
            const key = y * 12 + m;
            const fromKey = fiscalYearRange.fromYear * 12 + fiscalYearRange.fromMonth;
            const toKey = fiscalYearRange.toYear * 12 + fiscalYearRange.toMonth;
            return key >= fromKey && key <= toKey;
        }), [filteredAllocations, fiscalYearRange]);

    const futurePositionsByContractDate = useMemo(() => {
        if (!selectedLocation) return {};
        // Futures are often booked one tier up from the location tab they belong
        // to, so accept the selected location's parent as well as the location
        // itself. Without the null check a position at a top-level location
        // (parentLocationId null) would match any selected location that also
        // has no parent.
        const parentLocationId = selectedLocation.parentLocationId;
        const matchesSelectedLocation = (positionLocationId: number) =>
            positionLocationId === selectedLocation.locationId ||
            (parentLocationId != null && positionLocationId === parentLocationId);

        const futurePositions = displayedPositions.filter(p => {
            if (p.positionType !== PositionType.Future) return false;
            if (!matchesSelectedLocation(p.location.locationId)) return false;
            if (fiscalYearRange) {
                // The fiscal year a futures position belongs to is decided by the BUDGET
                // months it covers, not by its own contract month. A July corn contract
                // hedges May and June budget, so in a July-start fiscal year it belongs
                // to the year just gone - and the September contract, covering July and
                // August budget, is the one that belongs to this year. Filtering on the
                // contract month put each position a season out.
                const mappingsByBudgetMonth = getMappingsForCommodity(
                    contractMonthMappings, p.commodity?.commodityId);
                const budgetMonths = getBudgetMonthsForContractDate(
                    p.contractDate?.month, p.contractDate?.year, mappingsByBudgetMonth);

                if (budgetMonths.length === 0) return false;

                const fromKey = fiscalYearRange.fromYear * 12 + fiscalYearRange.fromMonth;
                const toKey = fiscalYearRange.toYear * 12 + fiscalYearRange.toMonth;
                // Any covered month inside the year brings the position in; the split
                // then puts only that month's share on a card.
                const covers = budgetMonths.some(budgetMonth => {
                    const key = budgetMonth.year * 12 + budgetMonth.month;
                    return key >= fromKey && key <= toKey;
                });

                if (!covers) return false;
            }
            return true;
        });

        const grouped = futurePositions.reduce((acc, position) => {
            if (!position.contractDate) return acc;
            const key = position.contractDate.label!;

            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(position);
            return acc;
        }, {} as Record<string, PositionModel[]>);

        // Sorted on the contract dates themselves, not by re-parsing the label. dayjs only
        // honours a format string with the customParseFormat plugin, which this app does
        // not register, so dayjs(label, 'MMM YYYY') fell back to lenient parsing and the
        // futures on the positions tab came out in whatever order the API returned them.
        const sortedKeys = Object.keys(grouped).sort((a, b) =>
            monthSortKey(grouped[a][0]?.contractDate) - monthSortKey(grouped[b][0]?.contractDate));

        const sortedGrouped: Record<string, PositionModel[]> = {};
        for (const key of sortedKeys) {
            sortedGrouped[key] = grouped[key];
        }

        return sortedGrouped;
    }, [displayedPositions, selectedLocation, fiscalYearRange, contractMonthMappings]);


    const [allDeliveries, setAllDeliveries] = useState<DeliveryModel[]>([]);

    // Fetch deliveries for all allocations when filteredAllocations changes
    /*useEffect(() => {
        const fetchAllDeliveries = async () => {
            if (!filteredAllocations.length) {
                setAllDeliveries([]);
                return;
            }
            // Fetch all deliveries for all allocations in parallel
            const promises = filteredAllocations.map(alloc =>
                CommodityManagementApi.listDeliveries(
                    loadingState.setLoadingState,
                    alloc.counterparty.customerCounterpartyCode!,
                    alloc.location.customerLocationCode!
                )
            );
            const results = await Promise.all(promises);
            // Flatten all deliveries into a single array
            const deliveries = results.flatMap(res => res.data.rows || []);
            setAllDeliveries(deliveries);
        };
        fetchAllDeliveries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredAllocations]);*/


    const fetchDeliveries = async (vendor: string, plant: string) => {
        const deliveryPromise = CommodityManagementApi.listDeliveries(loadingState.setLoadingState, vendor, plant)

        const [deliveryResponse] = await Promise.all([deliveryPromise]);

        setAllDeliveries(deliveryResponse.data.rows || []);
    }

    const forecastData = useMemo((): ForecastData => {
        if (!selectedCommodity || !selectedLocation) return { rows: [], forecastRounds: [] };

        const relevantBudgets = displayedBudgets.filter(b =>
            b.location.locationId === selectedLocation.locationId &&
            b.commodity.commodityId === selectedCommodity.commodityId
        );

        const forecastBudgets = relevantBudgets.filter(b => b.budgetRoundModel);

        const forecastRounds = Array.from(new Map(forecastBudgets.filter(f=>f.budgetRoundModel!.budgetRoundName != "Base").map(b => [b.budgetRoundModel!.budgetRoundId, b.budgetRoundModel!])).values())
            .sort((a, b) => dayjs(b.entryDate).valueOf() - dayjs(a.entryDate).valueOf());

        const monthlyData: Record<string, {
            budgetMonthLabel: string;
            sortKey: number;
            quantity?: number | null;
            unit?: Unit | null;
            forecasts: Record<number, { quantity?: number; note?: string; }>;
        }> = {};

        for (const budget of relevantBudgets) {
            const monthKey = `${budget.budgetDate.year}-${budget.budgetDate.month}`;
            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = {
                    budgetMonthLabel: dayjs(`${budget.budgetDate.year}-${budget.budgetDate.month}-01`).format('MMM-YY'),
                    sortKey: monthSortKey(budget.budgetDate),
                    forecasts: {},
                    unit: budget.unit,
                };
            }

            if (budget.budgetRoundModel.budgetRoundName != "Base") {
                monthlyData[monthKey].forecasts[budget.budgetRoundModel.budgetRoundId] = {
                    quantity: budget.volume!,
                    note: budget.note ?? undefined
                };
            } else {
                monthlyData[monthKey].quantity = budget.volume;
            }
        }

        const rows = Object.values(monthlyData).sort((a, b) => a.sortKey - b.sortKey);

        return { rows, forecastRounds };
    }, [displayedBudgets, selectedLocation, selectedCommodity]);

    const getFuturesForEfp = (relevantPositions: PositionModel[]/* summary: { month: string }*/) => {
        // Get all allocations from filtered allocations
        const allAllocations = filteredAllocations.flatMap(a => a.allocations);

        // Calculate allocated volume per position
        const allocatedVolumeByPositionId = allAllocations.reduce((acc, allocation) => {
            const positionId = allocation.positionId;
            acc[positionId] = (acc[positionId] || 0) + allocation.volume;
            return acc;
        }, {} as Record<number, number>);

        // Filter positions that still have volume left to allocate
        const positionsWithRemainingVolume = relevantPositions.filter(p => {
            const allocatedVolume = allocatedVolumeByPositionId[p.positionId!] || 0;
            const remainingVolume = (p.volume ?? 0) - allocatedVolume;
            return remainingVolume > 0;
        });

        setEfpPositions(positionsWithRemainingVolume);
        setEfpModalOpen(true);
    };

    const getPositionsForOffset = (relevantPositions: PositionModel[]/*summary: { month: string }*/) => {
        /*const monthDate = dayjs(summary.month, 'MMM-YY');
        const targetYear = monthDate.year();
        const targetMonth = monthDate.month() + 1;

        const relevantPositions = allPositions.filter(p =>
            p.budgetDate.year === targetYear &&
            p.budgetDate.month === targetMonth &&
            p.positionType === PositionType.Future &&
            p.commodity.commodityId === selectedCommodity?.commodityId
        );*/

        setOffsetPositions(relevantPositions);
        setOffsetModalOpen(true);
    };

    if (!customerSettings || !selectedLocation) {
        return <p>No child locations found for this dashboard.</p>;
    }

    return (
        <Box sx={{ width: '100%' }}>
            <EFPPositionsModal
                open={efpModalOpen}
                onClose={() => setEfpModalOpen(false)}
                positions={efpPositions}
                allocations={filteredAllocations}
                onSuccess={fetchData}
                customerSettings={customerSettings}
                units={units || []}
                selectedDisplayUnit={selectedDisplayUnit}
            />
            <OffsetPositionsModal
                open={offsetModalOpen}
                onClose={() => setOffsetModalOpen(false)}
                positions={offsetPositions}
                onSuccess={fetchData}
                locations={locations || []}
                allocations={filteredAllocations}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 2 }}>
                <Tabs value={locationTab} onChange={handleLocationChange} aria-label="location tabs" className="location-tabs">
                    {childLocations.map(loc => (
                        <Tab label={loc.locationName} key={loc.locationId} className="location-tab" />
                    ))}
                </Tabs>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl sx={{ minWidth: 120 }} size="small">
                        <InputLabel id="unit-select-label">Unit</InputLabel>
                        <Select
                            labelId="unit-select-label"
                            id="unit-select"
                            value={selectedDisplayUnit || ''}
                            label="Unit"
                            onChange={(e) => setSelectedDisplayUnit(e.target.value as Unit)}
                        >
                             {units?.map((unit: any) => (
                                <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 120 }} size="small">
                        <InputLabel id="fiscal-year-label">Fiscal Year</InputLabel>
                        <Select
                            labelId="fiscal-year-label"
                            id="fiscal-year-select"
                            value={selectedFiscalYear}
                            label="Fiscal Year"
                            onChange={(e) => setSelectedFiscalYear(e.target.value)}
                        >
                            {fiscalYearOptions.map(year => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <MissingPricingComponentsPrompt positions={allPositions} onStrategySaved={fetchData} />

            {loadingData ? <StoneXLoading show /> : (
                <>
                    {commoditiesForSelectedLocation && commoditiesForSelectedLocation.length > 0 ? (
                        <Box className="commodity-tabs-container">
                            <Tabs value={commodityTab} onChange={handleCommodityChange} aria-label="commodity tabs" className="commodity-tabs">
                                {commoditiesForSelectedLocation.map(commodity => (
                                    <Tab
                                        label={commodity.commodityName}
                                        key={commodity.commodityId}
                                        className="commodity-tab"
                                    />
                                ))}
                            </Tabs>
                        </Box>
                    ) : <p>No commodities found for this location's budget.</p>}

                    {commoditiesForSelectedLocation && commoditiesForSelectedLocation.length > 0 && (
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={featureTab} onChange={handleFeatureChange} aria-label="feature tabs" sx={{ pl: 8 }}>
                                {featureTabs.map(tabName => (
                                    <Tab label={tabName} key={tabName} />
                                ))}
                            </Tabs>
                        </Box>
                    )}

                    {commoditiesForSelectedLocation && commoditiesForSelectedLocation.map((commodity, cIndex) => (
                        <TabPanel value={commodityTab} index={cIndex} key={commodity.commodityId}>
                            <TabPanel value={featureTab} index={0}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" component="div">
                                        Suppliers
                                    </Typography>
                                    <IconButton onClick={() => setAddSupplierModalOpen(true)} size="small">
                                        <AddIcon />
                                    </IconButton>
                                </Box>
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Supplier</TableCell>
                                                <TableCell>Start Month</TableCell>
                                                <TableCell>End Month</TableCell>
                                                <TableCell>Display Unit</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {processedAllocations.map(alloc => (
                                                <TableRow key={alloc.allocationGroupId}>
                                                    <TableCell>{alloc.counterparty.counterpartyName}</TableCell>
                                                    <TableCell>{alloc.startMonth}</TableCell>
                                                    <TableCell>{alloc.endMonth}</TableCell>
                                                    <TableCell>{alloc.unit}</TableCell>
                                                </TableRow>
                                            ))}

                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <StoneXDivider />
                                <StoneXRow align='center'>
                                    <h2>Summary</h2>
                                </StoneXRow>
                                <SummaryCards
                                    futures={Object.values(futurePositionsByContractDate).flat()}
                                    contractMonthMappings={contractMonthMappings}
                                    positionsByAllocation={positionsByAllocation}
                                    forecast={latestForecast}
                                    months={fiscalYearMonths}
                                    commodityId={selectedCommodity?.commodityId}
                                    deliveries={filteredAllocations.flatMap(a => a.deliveries ?? [])}
                                    displayUnit={selectedDisplayUnit}
                                />
                            </TabPanel>
                            <TabPanel value={featureTab} index={1}>
                                {budgetsLoadingState.isLoaded() ? (
                                    <BudgetTable budgets={budgetTableRows} onViewAuditTrail={handleViewAuditTrail} onEditBudget={handleEditBudget} defaultExpanded={true} />
                                ) : <StoneXLoading show />}
                            </TabPanel>
                            <TabPanel value={featureTab} index={2}>
                                <ForecastTable
                                    forecastData={forecastData}
                                    onForecastSave={fetchData}
                                    budgets={displayedBudgets}
                                    originalBudgets={budgets}
                                    selectedLocationId={selectedLocation.locationId}
                                    selectedCommodityId={selectedCommodity.commodityId}
                                    displayUnit={selectedDisplayUnit}
                                />
                            </TabPanel>
                            <TabPanel value={featureTab} index={3}>
                                <Ledger
                                    filteredAllocations={filteredAllocations}
                                    fiscalYearMonths={fiscalYearMonths}
                                    selectedDisplayUnit={selectedDisplayUnit}
                                    onRollSuccess={fetchData}
                                />
                            </TabPanel>
                            <TabPanel value={featureTab} index={4}>
                              <PositionsTab
                                futurePositionsByContractDate={futurePositionsByContractDate}
                                detailsVisibility={detailsVisibility}
                                setDetailsVisibility={setDetailsVisibility}
                                getFuturesForEfp={getFuturesForEfp}
                                getPositionsForOffset={getPositionsForOffset}
                                customerSettings={customerSettings}
                                columnsToShow={columnsToShow}
                                editDealTicket={editDealTicket}
                                setPositionValidation={setPositionValidation}
                                viewAuditTrail={viewAuditTrail}
                                positionsByAllocation={positionsByAllocation}
                                units={units}
                                allocations={filteredAllocations}
                                allPositions={allPositions}
                                onCustomDataKeysChange={setCustomDataKeys}
                              />
                            </TabPanel>
                            <TabPanel value={featureTab} index={5}>
                                <Box sx={{ mb: 2 }}>
                                    <FormControl sx={{ minWidth: 220 }} size="small">
                                        <InputLabel id="dashboard-report-label">Report</InputLabel>
                                        <Select
                                            labelId="dashboard-report-label"
                                            value={reportView}
                                            label="Report"
                                            onChange={(e) => setReportView(e.target.value as 'deliveries' | 'futuresAllocation')}
                                        >
                                            <MenuItem value="futuresAllocation">Futures Allocation</MenuItem>
                                            <MenuItem value="deliveries">Deliveries</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                {reportView === 'deliveries' && (
                                    <DeliveriesData
                                        allocations={rawFilteredAllocations}
                                        displayUnit={selectedDisplayUnit}
                                    />
                                )}
                                {reportView === 'futuresAllocation' && locationId && (
                                    <FuturesAllocationReport
                                        parentLocationId={Number(locationId)}
                                        locations={locations ?? []}
                                        childLocations={childLocations}
                                        commodity={selectedCommodity}
                                        budgets={budgets}
                                        fiscalYearMonths={fiscalYearMonths}
                                        contractMonthMappings={contractMonthMappings}
                                        units={units ?? undefined}
                                        locationIdsWithoutEfp={futuresAllocationSettings.locationIdsWithoutEfp}
                                    />
                                )}
                            </TabPanel>
                            <TabPanel value={featureTab} index={6}>
                                {selectedLocation && selectedCommodity && (
                                    <DocumentsTab
                                        locationId={selectedLocation.locationId}
                                        commodityId={selectedCommodity.commodityId}
                                    />
                                )}
                            </TabPanel>
                        </TabPanel>
                    ))}

                </>
            )}
            {selectedCommodity && (
                <AddSupplierModal
                    open={addSupplierModalOpen}
                    onClose={() => setAddSupplierModalOpen(false)}
                    onSuccess={fetchData}
                    locationId={selectedLocation.locationId}
                    commodityId={selectedCommodity.commodityId}
                    unit={budgetTableRows[0]?.unit!}
                    fiscalYear={selectedFiscalYear}
                    fiscalMonthOffset={customerSettings.fiscalMonthOffset ?? 0}
                    allCounterparties={allCounterparties || []}
                    existingCounterpartyIds={filteredAllocations.map(a => a.counterparty.counterpartyId)}
                    units={units || [] }
                />
            )}
            <UpdatePositionsModal
                dealTicketId={editDealTicketId}
                additionalCustomDataKeys={customDataKeys}
                open={editDealTicketModalOpen}
                onClose={() => setEditDealTicketModalOpen(false)}
                onSave={onDealTicketSave}
                onDelete={onDealTicketDelete}
            />

            <PositionValidationModal
                open={validationModalOpen}
                dealTicketId={validationDealTicketId}
                onClose={() => setValidationModalOpen(false)}
                onValidationUpdated={onValidationUpdated}
            />

            <ViewAuditTrailModal
                AuditTrailModels={AuditTrailModels}
                open={auditTrailModalIsOpen}
                isLoading={auditLoadingState.isLoading()}
                onClose={closeAuditTrailModal}
            />

        </Box>
    );
};

export default Dashboard;
